import {
  heroDescription,
  heroTagline,
  signupLink,
  bookDemoLink,
} from '@/config'
import { PrimaryButton, SecondaryButton } from './Button'
import { PostLayoutDivider } from './PostLayoutDivider'
import { cloudflareImages, googleCloudImages } from './markdoc/Images'
import { Video } from './markdoc/Video'
import Image from 'next/image'

function ImageContainer({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>
}

const mediaOptions = {
  workloads: (
    <ImageContainer>
      <Image
        src={cloudflareImages['productBannerFeb2025'].cloudflareImageId}
        width={cloudflareImages['productBannerFeb2025'].width}
        height={cloudflareImages['productBannerFeb2025'].height}
        alt="SELECT web application screenshot"
        className=""
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  budgets: (
    <ImageContainer>
      <Image
        src={cloudflareImages['budgets'].cloudflareImageId}
        width={cloudflareImages['budgets'].width}
        height={cloudflareImages['budgets'].height}
        alt="SELECT web application screenshot"
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  insights: (
    <ImageContainer>
      <Image
        src={cloudflareImages['insights1'].cloudflareImageId}
        width={cloudflareImages['insights1'].width}
        height={cloudflareImages['insights1'].height}
        alt="SELECT web application screenshot"
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  slackNotification: (
    <ImageContainer>
      <Image
        src={cloudflareImages['slackNotification'].cloudflareImageId}
        width={cloudflareImages['slackNotification'].width}
        height={cloudflareImages['slackNotification'].height}
        alt="SELECT slack notification"
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  usageGroupsFeature: (
    <ImageContainer>
      <Image
        src={cloudflareImages['usageGroupsFeature'].cloudflareImageId}
        width={cloudflareImages['usageGroupsFeature'].width}
        height={cloudflareImages['usageGroupsFeature'].height}
        alt="SELECT usage groups feature"
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  snowpipeRecommendation: (
    <ImageContainer>
      <Image
        src={cloudflareImages['snowpipeRecommendation'].cloudflareImageId}
        width={cloudflareImages['snowpipeRecommendation'].width}
        height={cloudflareImages['snowpipeRecommendation'].height}
        alt="SELECT optimization recommendations"
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  automatedSavings: (
    <ImageContainer>
      <Image
        src={cloudflareImages['automatedSavings'].cloudflareImageId}
        width={cloudflareImages['automatedSavings'].width}
        height={cloudflareImages['automatedSavings'].height}
        alt="SELECT automated savings feature"
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </ImageContainer>
  ),
  lookerDashboardTimeline: (
    <ImageContainer>
      <Image
        src={`https://storage.googleapis.com/prod_select_images/${googleCloudImages['lookerDashboard'].filename}`}
        alt="SELECT Looker dashboard timeline view"
        width={googleCloudImages['lookerDashboard'].width}
        height={googleCloudImages['lookerDashboard'].height}
        className="rounded-lg ring-2 ring-white/20"
        priority={true}
        unoptimized // required to force NextJS to not use the custom cloudflare loader
      />
    </ImageContainer>
  ),
  dbtVideo: (
    <Video
      divClassName="ring-2 ring-white/20 overflow-hidden rounded-md"
      googleCloudKey={'marketing/dbt-overview'}
    />
  ),
}

export function ProductBanner({
  includeTopDivider = true,
  title = heroTagline,
  subtitle = 'Get up and running with SELECT in 15 minutes.',
  description = heroDescription,
  mediaKey = 'workloads',
}: {
  includeTopDivider?: boolean;
  title?: string;
  subtitle?: string;
  description?: string;
  mediaKey?: keyof typeof mediaOptions;
}) {
  return (
    <>
      {includeTopDivider && <PostLayoutDivider />}
      <div className="mx-auto flex flex-wrap gap-x-8 gap-y-12 lg:flex-nowrap">
        <div className="m-auto max-w-md space-y-6 text-center sm:shrink-0  lg:text-left">
          <p className="font-semibold text-white">{subtitle}</p>
          <h1 className="bg-gradient-to-r from-indigo-200 via-sky-400 to-indigo-200 bg-clip-text text-2xl font-bold tracking-tight text-transparent lg:text-3xl">
            {title}
          </h1>
          <p className="text-md leading-8 text-gray-300">{description}</p>
          <div className="flex justify-center gap-x-4 pt-2 lg:justify-start">
            <PrimaryButton textSize="text-sm" href={signupLink}>
              Get access now →
            </PrimaryButton>
            <SecondaryButton
              target="_blank"
              href={bookDemoLink}
              textSize="text-sm"
            >
              Book a Demo
            </SecondaryButton>
          </div>
        </div>
        {mediaOptions[mediaKey]}
      </div>
    </>
  )
}
